import React, { useCallback, useMemo } from "react";
import { noop } from "@common/helpers/function";
import {
    ENGAGEMENT_TEAM_ROLES,
    EngagementTeamRole,
    TaskTemplateAssigneeRole,
} from "@common/types/taskTemplate";
import { useCurrentOrganizationMembers } from "@api/organizationMember.api";
import {
    FilterMultiselect,
    FilterMultiselectOption,
    FilterMultiselectValue,
} from "@components/pageHeader/filters/FilterMultiselect/FilterMultiselect";
import { FilterActionsFooter } from "@design-system/Filter/FilterActionsFooter/FilterActionsFooter";
import { BaseFilterProps } from "../../lib";
import { ModeRadioGroup } from "../ReviewFilter/ModeRadioGroup";
import styles from "../ReviewFilter/ReviewFilter.module.scss";

const ROLE_LABELS: Record<EngagementTeamRole, string> = {
    [TaskTemplateAssigneeRole.PREPARER]: "Preparer",
    [TaskTemplateAssigneeRole.REVIEWER]: "Reviewer",
    [TaskTemplateAssigneeRole.MANAGER]: "Manager",
};

const ROLE_OPTIONS: FilterMultiselectOption[] = ENGAGEMENT_TEAM_ROLES.map(
    (role) => ({
        value: role,
        label: ROLE_LABELS[role],
    }),
);

const ROLE_MODE_OPTIONS: Array<{ value: "any" | "every"; label: string }> = [
    { value: "any", label: "Any role" },
    { value: "every", label: "Every role" },
];

const MEMBER_MODE_OPTIONS: Array<{ value: "any" | "every"; label: string }> = [
    { value: "any", label: "Any member" },
    { value: "every", label: "Every member" },
];

export const EngagementTeamFilter: React.FC<BaseFilterProps> = ({
    filters,
    onChange,
}) => {
    const { data: orgMembers } = useCurrentOrganizationMembers();

    const roles = useMemo(
        () => filters.engagementTeamRoles ?? [],
        [filters.engagementTeamRoles],
    );
    const roleOperator = filters.engagementTeamRoleOperator ?? "any";
    const memberIds = useMemo(
        () => filters.engagementTeamMemberIds ?? [],
        [filters.engagementTeamMemberIds],
    );
    const memberOperator = filters.engagementTeamMemberOperator ?? "any";

    const memberOptions = useMemo(
        (): FilterMultiselectOption[] =>
            (orgMembers ?? []).map((m) => ({
                value: m.user.id,
                label: m.user.name,
                searchableLabel: m.user.email
                    ? `${m.user.name} ${m.user.email}`
                    : m.user.name,
            })),
        [orgMembers],
    );

    const isFilterActive = roles.length > 0 || memberIds.length > 0;

    const handleReset = useCallback(() => {
        onChange({
            engagementTeamRoles: undefined,
            engagementTeamRoleOperator: undefined,
            engagementTeamMemberIds: undefined,
            engagementTeamMemberOperator: undefined,
        });
    }, [onChange]);

    const reverseMembers = useCallback(() => {
        if (memberIds.length > 0 && orgMembers) {
            onChange({
                engagementTeamMemberIds: orgMembers
                    .filter((m) => !memberIds.includes(m.user.id))
                    .map((m) => m.user.id),
            });
        }
    }, [memberIds, orgMembers, onChange]);

    return (
        <div className={styles.filter}>
            {/* Role section */}
            <ModeRadioGroup
                name="engagementTeamRoleOperator"
                value={roleOperator}
                options={ROLE_MODE_OPTIONS}
                onChange={(value) =>
                    onChange({ engagementTeamRoleOperator: value })
                }
            />
            <div className={styles.sectionWithTopBorder}>
                <FilterMultiselect
                    inDropdown={false}
                    searchable={false}
                    options={ROLE_OPTIONS}
                    value={roles}
                    onChange={(val: FilterMultiselectValue[]) =>
                        onChange({
                            engagementTeamRoles:
                                val.length > 0
                                    ? (val as EngagementTeamRole[])
                                    : undefined,
                            engagementTeamRoleOperator:
                                val.length === 0 ? undefined : roleOperator,
                        })
                    }
                    name="engagementTeamRoles"
                    onBlur={noop}
                />
            </div>

            {/* Member section */}
            <div className={styles.sectionWithTopBorder}>
                <ModeRadioGroup
                    name="engagementTeamMemberOperator"
                    value={memberOperator}
                    options={MEMBER_MODE_OPTIONS}
                    onChange={(value) =>
                        onChange({ engagementTeamMemberOperator: value })
                    }
                />
            </div>
            <div className={styles.sectionWithTopBorder}>
                <FilterMultiselect
                    inDropdown={false}
                    options={memberOptions}
                    value={memberIds}
                    onChange={(val: FilterMultiselectValue[]) =>
                        onChange({
                            engagementTeamMemberIds:
                                val.length > 0 ? (val as number[]) : undefined,
                            engagementTeamMemberOperator:
                                val.length === 0 ? undefined : memberOperator,
                        })
                    }
                    name="engagementTeamMemberIds"
                    onBlur={noop}
                    filterFn={(search, option) => {
                        const q = search.toLowerCase();
                        const target =
                            option.searchableLabel ??
                            String(option.label ?? "");
                        return target.toLowerCase().includes(q);
                    }}
                />
            </div>

            {isFilterActive && (
                <FilterActionsFooter
                    deselectFunction={handleReset}
                    selectedCount={roles.length + memberIds.length}
                    reverseFunction={
                        memberIds.length > 0 ? reverseMembers : undefined
                    }
                />
            )}
        </div>
    );
};
